import { HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Stack,
  Text,
  Textarea
} from "@chakra-ui/react";
import {
  FiFileText,
  FiImage,
  FiMessageSquare,
  FiMic,
  FiSettings,
  FiUsers,
  FiVideo,
  FiClipboard
} from "react-icons/fi";
import { Dropdown } from "components/dropdown";
import { DropdownField } from "components/dropdown-field";
import { InputField } from "components/input-field";
import { DraggableFileField } from "components/input-file";
import { FilterCard, FilterGrid } from "../components/FilterCard";
import { SidePanelAccordion } from "../components/SidePanelAccordion";
import { State } from "components/pipeline-components/classes/state-machine/state";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { Popover } from "components/popover";
import { DocumentIcon2 } from "components/vectors/document-icon";
import {
  csvFields,
  firstCsvRow
} from "components/campanha-sms-components/modal-campanha-sms/csv-cols";
import api from "api/api";
import { useEffect, useState } from "react";
import { formatFileSize } from "utils/string-formats";
import { Toast } from "components/toast";
import { BiMicrophone } from "react-icons/bi";
import { DiagnosticPanel } from "./DiagnosticPanel";

interface WhatsAppSession {
  id: number;
  nome: string;
}

const typeOptions = [
  { name: "Texto", value: "TEXT", icon: FiFileText },
  { name: "Imagem", value: "IMAGE", icon: FiImage },
  { name: "Áudio", value: "AUDIO", icon: BiMicrophone },
  { name: "Vídeo", value: "VIDEO", icon: FiVideo },
  { name: "Doc.", value: "DOCUMENT", icon: FiClipboard }
];

const fileExtensionsByType: Record<string, string[]> = {
  IMAGE: ["image/png", "image/jpeg", "image/webp"],
  AUDIO: ["audio/mpeg", "audio/ogg"],
  VIDEO: ["video/mp4", "video/3gpp"],
  DOCUMENT: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ]
};

const fileTypeFromWhatsType = (
  type?: string
): "audio" | "video" | "image" | "document" | undefined => {
  if (!type) return undefined;
  const map: Record<string, "audio" | "video" | "image" | "document"> = {
    AUDIO: "audio",
    VIDEO: "video",
    IMAGE: "image",
    DOCUMENT: "document"
  };
  return map[type];
};

const MAX_FILE_SIZE_VIDEO = 16 * 1024 * 1024;
const MAX_FILE_SIZE_OTHER = 5 * 1024 * 1024;

interface WhatsAppSession {
  id: number;
  nome: string;
}

export function WhatsEditor({
  isDisabled,
  whatsState,
  updatePipelineData,
  midiaFileWpp,
  onMidiaFileWppChange,
  selectedStateId,
  pipelineData,
  diagnosticUpdatePipelineData
}: {
  isDisabled: boolean;
  whatsState?: State;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  midiaFileWpp?: File;
  onMidiaFileWppChange?: (file: File | undefined) => void;
  selectedStateId?: number;
  pipelineData?: StatesMachine;
  diagnosticUpdatePipelineData?: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}) {
  const [whatsAppPhones, setWhatsAppPhones] = useState<WhatsAppSession[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [csvSearchSaudacao, setCsvSearchSaudacao] = useState("");
  const [csvSearchMensagem, setCsvSearchMensagem] = useState("");

  const csvCols = csvFields.map((key) => ({ name: key, value: key }));
  const filteredCsvColsSaudacao = csvCols.filter(({ name }) =>
    name.toLowerCase().includes(csvSearchSaudacao.toLowerCase())
  );
  const filteredCsvColsMensagem = csvCols.filter(({ name }) =>
    name.toLowerCase().includes(csvSearchMensagem.toLowerCase())
  );

  const currentType = whatsState?.type ?? "TEXT";

  useEffect(() => {
    const getPhones = async () => {
      setLoadingPhones(true);
      try {
        const { data } = await api.get("/whatsapp-sessions?only_disparo=true");
        setWhatsAppPhones(data);
      } catch {
      } finally {
        setLoadingPhones(false);
      }
    };
    getPhones();
  }, []);

  const getUsedHeadersFromMessage = (mensagem: string) => {
    return csvFields
      .filter((field) => mensagem.includes(`#${field}#`))
      .join(";");
  };

  const getPreVisualizacao = () => {
    let preVisualizacao = whatsState?.mensagemNormal || "";
    csvCols.forEach(({ value }) => {
      preVisualizacao = preVisualizacao.replaceAll(
        `#${value}#`,
        firstCsvRow[value] || value
      );
    });
    return preVisualizacao;
  };

  const updateWhats = (updater: (state: State) => void) => {
    updatePipelineData((draft) => {
      draft.updateOperationState("WHATS", updater);
    });
  };

  const selectedSessionsLength = whatsState?.wppSessionIds?.length ?? 0;

  const whatsAppSessionsLabel =
    selectedSessionsLength > 1
      ? `${selectedSessionsLength} Participantes`
      : (whatsAppPhones.find(({ id }) => id === whatsState?.wppSessionIds?.[0])
          ?.nome ?? "Selecione");

  const selectWhatsAppSession = (value: number) => {
    const current = whatsState?.wppSessionIds ?? [];
    const isIncluded = current.includes(value);
    const next = isIncluded
      ? current.filter((id) => id !== value)
      : [...current, value];

    updateWhats((state) => {
      state.wppSessionIds = next;
    });
  };

  return (
    <Stack spacing="12px" mt="10px">
      {/* Configuração */}
      <SidePanelAccordion
        icon={FiSettings}
        iconBgColor="#EDE9FE"
        iconColor="#7C3AED"
        title="Configuração"
        subtitle="Nome e quantidade estimada de envios"
        activeCount={0}
      >
        <FilterGrid>
          <FilterCard
            label="Nome da campanha"
            isDisabled={isDisabled}
            fullWidth
          >
            <InputField
              isDisabled={isDisabled}
              variant="filterInput"
              value={whatsState?.name ?? ""}
              onChange={(event) =>
                updateWhats((state) => {
                  state.name = event.target.value;
                })
              }
            />
          </FilterCard>

          <FilterCard
            label="Qtd. estimada de envio por dia"
            isDisabled={isDisabled}
            fullWidth
          >
            <InputField
              isDisabled={isDisabled}
              variant="filterInput"
              value={whatsState?.qtdEstimadaEnvioDia?.toString() ?? ""}
              inputProps={{ type: "number" }}
              onChange={(event) => {
                const parse = Number(event.target.value);
                if (parse > 500) event.currentTarget.value = "500";
                updateWhats((state) => {
                  state.qtdEstimadaEnvioDia = Number(event.target.value || 0);
                });
              }}
            />
          </FilterCard>
        </FilterGrid>
      </SidePanelAccordion>

      {/* Mensagem */}
      <SidePanelAccordion
        icon={FiMessageSquare}
        iconBgColor="#DBEAFE"
        iconColor="#2563EB"
        title="Mensagem"
        subtitle="Tipo, conteúdo e pré-visualização"
        activeCount={0}
      >
        <FilterGrid>
          <FilterCard
            label="Mensagem Saudação"
            isDisabled={isDisabled}
            fullWidth
          >
            <Dropdown
              w="100%"
              variant="filterInput"
              options={[
                { name: "Sim", value: true },
                { name: "Não", value: false }
              ]}
              onChange={(value: boolean) =>
                updateWhats((state) => {
                  state.enabledMensagemSaudacao = value;
                })
              }
              value={whatsState?.enabledMensagemSaudacao ?? false}
              isDisabled={isDisabled}
            />
          </FilterCard>

          {whatsState?.enabledMensagemSaudacao && (
            <FilterCard
              label="Texto da saudação"
              isDisabled={isDisabled}
              fullWidth
            >
              <Flex mb="8px" justify="flex-end">
                <Popover
                  title="Adicione campos do CSV"
                  button={
                    <Button
                      aria-label=""
                      size="xs"
                      variant="outline"
                      leftIcon={<HamburgerIcon />}
                      isDisabled={isDisabled}
                      bg="#fff"
                      boxShadow={
                        "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                      }
                    >
                      Colunas CSV
                    </Button>
                  }
                  popupStyles={{ width: "auto" }}
                  position="left-bottom"
                  isDisabled={isDisabled}
                >
                  <Box mb="8px">
                    <InputField
                      variant="filterInput"
                      value={csvSearchSaudacao}
                      onChange={(e) => setCsvSearchSaudacao(e.target.value)}
                      inputProps={{ placeholder: "Pesquisar coluna..." }}
                    />
                  </Box>
                  <Grid
                    w="100%"
                    gap="8px"
                    templateColumns="1fr"
                    overflow="auto"
                    maxH="200px"
                  >
                    {filteredCsvColsSaudacao.map(({ name, value }) => (
                      <Button
                        key={value}
                        onClick={() => {
                          const nextMessage = whatsState?.mensagemSaudacao
                            ? `${whatsState.mensagemSaudacao}#${value}#`
                            : `#${value}#`;

                          updateWhats((state) => {
                            state.mensagemSaudacao = nextMessage;
                          });
                        }}
                        size="sm"
                        variant="outline"
                        isDisabled={isDisabled}
                        bg="#fff"
                        boxShadow={
                          "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                        }
                      >
                        {name}
                      </Button>
                    ))}
                  </Grid>
                </Popover>
              </Flex>

              <InputField
                isDisabled={isDisabled}
                variant="filterInput"
                value={whatsState?.mensagemSaudacao ?? ""}
                onChange={(event) => {
                  const mensagem = event.target.value;
                  updateWhats((state) => {
                    state.mensagemSaudacao = mensagem;
                  });
                }}
                inputProps={{
                  as: "textarea",
                  height: "118px",
                  p: "10px 16px"
                }}
              />
            </FilterCard>
          )}

          <FilterCard label="Tipo" isDisabled={isDisabled} fullWidth>
            <Grid templateColumns="repeat(5, 1fr)" gap="8px">
              {typeOptions.map(({ name, value, icon }) => {
                const isSelected = currentType === value;
                return (
                  <Flex
                    key={value}
                    as="button"
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      onMidiaFileWppChange?.(undefined);
                      updateWhats((state) => {
                        state.type = value;
                        if (value !== "TEXT") {
                          state.mensagemNormal = undefined;
                          state.usedHeaders = undefined;
                        }
                        if (value === "TEXT") {
                          state.fileType = undefined;
                          state.filename = undefined;
                          state.fileSize = undefined;
                        }
                      });
                    }}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="2px"
                    p="6px 4px"
                    borderRadius="10px"
                    minW="0"
                    border="1px solid"
                    borderColor={
                      isSelected
                        ? "var(--chakra-colors-primary-400)"
                        : "#E2E8F0"
                    }
                    bg={isSelected ? "#fff" : "#fff"}
                    cursor={isDisabled ? "not-allowed" : "pointer"}
                    opacity={isDisabled ? 0.6 : 1}
                    _hover={
                      !isDisabled
                        ? {
                            bg: isSelected
                              ? undefined
                              : "var(--chakra-colors-gray-50)"
                          }
                        : {}
                    }
                    transition="all 0.15s"
                    boxShadow={
                      "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                    }
                  >
                    <Icon
                      as={icon}
                      boxSize="14px"
                      color={
                        isSelected
                          ? "var(--chakra-colors-primary-400)"
                          : "var(--chakra-colors-gray-500)"
                      }
                    />
                    <Text
                      fontSize="10px"
                      fontWeight={isSelected ? "600" : "normal"}
                      noOfLines={1}
                      color={
                        isSelected
                          ? "var(--chakra-colors-primary-400)"
                          : "var(--chakra-colors-gray-500)"
                      }
                    >
                      {name}
                    </Text>
                  </Flex>
                );
              })}
            </Grid>
          </FilterCard>

          {currentType === "TEXT" ? (
            <>
              <FilterCard label="Mensagem" isDisabled={isDisabled} fullWidth>
                <Flex mb="8px" justify="flex-end">
                  <Popover
                    title="Adicione campos do CSV"
                    button={
                      <Button
                        aria-label=""
                        size="xs"
                        variant="outline"
                        leftIcon={<HamburgerIcon />}
                        isDisabled={isDisabled}
                        bg="#fff"
                        boxShadow={
                          "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                        }
                      >
                        Colunas CSV
                      </Button>
                    }
                    popupStyles={{ width: "auto" }}
                    position="left-bottom"
                    isDisabled={isDisabled}
                  >
                    <Box mb="8px">
                      <InputField
                        variant="filterInput"
                        value={csvSearchMensagem}
                        onChange={(e) => setCsvSearchMensagem(e.target.value)}
                        inputProps={{ placeholder: "Pesquisar coluna..." }}
                      />
                    </Box>
                    <Grid
                      w="100%"
                      gap="8px"
                      templateColumns="1fr"
                      overflow="auto"
                      maxH="200px"
                    >
                      {filteredCsvColsMensagem.map(({ name, value }) => (
                        <Button
                          key={value}
                          onClick={() => {
                            const nextMessage = whatsState?.mensagemNormal
                              ? `${whatsState.mensagemNormal}#${value}#`
                              : `#${value}#`;

                            updateWhats((state) => {
                              state.mensagemNormal = nextMessage;
                              state.usedHeaders =
                                getUsedHeadersFromMessage(nextMessage);
                              state.type = "TEXT";
                            });
                          }}
                          size="sm"
                          variant="outline"
                          isDisabled={isDisabled}
                          bg="#fff"
                          boxShadow={
                            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                          }
                        >
                          {name}
                        </Button>
                      ))}
                    </Grid>
                  </Popover>
                </Flex>

                <InputField
                  isDisabled={isDisabled}
                  variant="filterInput"
                  value={whatsState?.mensagemNormal ?? ""}
                  onChange={(event) => {
                    const mensagem = event.target.value;
                    updateWhats((state) => {
                      state.mensagemNormal = mensagem;
                      state.usedHeaders = getUsedHeadersFromMessage(mensagem);
                      state.type = "TEXT";
                    });
                  }}
                  inputProps={{
                    as: "textarea",
                    height: "118px",
                    p: "10px 16px"
                  }}
                />
              </FilterCard>

              <FilterCard
                label="Pré-visualização"
                isDisabled={isDisabled}
                fullWidth
              >
                <Textarea
                  value={getPreVisualizacao()}
                  disabled
                  height="130px"
                  autoComplete="new-password"
                  border="1px solid"
                  borderColor="#E2E8F0"
                  borderRadius="10px"
                  fontSize="13px"
                  bg="#F8FAFC"
                  color="#90a1b9"
                  _focus={{ outline: "none", boxShadow: "none" }}
                  _hover={{}}
                />
              </FilterCard>
            </>
          ) : (
            <FilterCard label="Arquivo" isDisabled={isDisabled} fullWidth>
              {isDisabled ? (
                whatsState?.filename ? (
                  <Flex
                    bg="#fff"
                    p="10px 15px"
                    gap="10px"
                    borderRadius="0.25rem"
                    alignItems="center"
                    border="1px solid"
                    borderColor="var(--chakra-colors-custom-gray)"
                    opacity="0.6"
                  >
                    <DocumentIcon2 width="33px" height="33px" />
                    <Box>
                      <Text fontWeight="bold" fontSize="17">
                        {whatsState.filename}
                      </Text>
                      <Text>
                        {whatsState.fileSize != null
                          ? formatFileSize(whatsState.fileSize)
                          : ""}
                      </Text>
                    </Box>
                  </Flex>
                ) : (
                  <Text color="#90a1b9" fontSize="sm">
                    Nenhum arquivo carregado
                  </Text>
                )
              ) : (
                <DraggableFileField
                  file={midiaFileWpp}
                  handleFile={(file) => {
                    const maxSize =
                      currentType === "VIDEO"
                        ? MAX_FILE_SIZE_VIDEO
                        : MAX_FILE_SIZE_OTHER;
                    if (file.size > maxSize) {
                      const limitLabel =
                        currentType === "VIDEO" ? "16MB" : "5MB";
                      Toast({
                        title: `Arquivo excede o limite de ${limitLabel}`,
                        status: "error"
                      });
                      return;
                    }
                    onMidiaFileWppChange?.(file);
                    updateWhats((state) => {
                      state.fileType = fileTypeFromWhatsType(currentType);
                      state.filename = file.name;
                      state.fileSize = file.size;
                    });
                  }}
                  handleDelete={() => {
                    onMidiaFileWppChange?.(undefined);
                    updateWhats((state) => {
                      state.fileType = undefined;
                      state.filename = undefined;
                      state.fileSize = undefined;
                    });
                  }}
                  error=""
                  fileExtensions={fileExtensionsByType[currentType] ?? []}
                  disabled={isDisabled}
                />
              )}
            </FilterCard>
          )}
        </FilterGrid>
      </SidePanelAccordion>

      {/* Participantes */}
      <SidePanelAccordion
        icon={FiUsers}
        iconBgColor="#D1FAE5"
        iconColor="#059669"
        title="Participantes"
        subtitle="Sessões do WhatsApp para disparo"
        activeCount={0}
      >
        <FilterGrid>
          <FilterCard
            label="WhatsApp participantes"
            isDisabled={isDisabled}
            fullWidth
          >
            <DropdownField
              dropdownProps={{
                w: "100%",
                variant: "filterInput",
                options: whatsAppPhones,
                isLoading: loadingPhones,
                optionValue: "id",
                optionLabel: "nome",
                multiSelect: true,
                children: whatsAppSessionsLabel,
                isDisabled: isDisabled
              }}
              onChange={selectWhatsAppSession}
              value={whatsState?.wppSessionIds ?? []}
            />
          </FilterCard>
        </FilterGrid>
      </SidePanelAccordion>
      {selectedStateId != null &&
        pipelineData &&
        diagnosticUpdatePipelineData && (
          <DiagnosticPanel
            selectedStateId={selectedStateId}
            pipelineData={pipelineData}
            operation="WHATS"
            updatePipelineData={diagnosticUpdatePipelineData}
          />
        )}
    </Stack>
  );
}
